import { useCallback, useMemo } from "react"
import type { JSONSchema7Type } from "json-schema"
import { TextInput } from "resha"
import { turbine } from "neql"
import { ExclusiveChoiceDropdown } from "dash-styled"

import { useNeqlContextProvider } from "../../../../Hooks/useNeqlContextProvider"
import { useAppState } from "../../../../Hooks/useReduxState"

export function TrainingSelectWidget({
    readOnly,
    value,
    defaultValue,
    onChange
}: {
    readOnly: boolean
    value: number | undefined
    defaultValue: JSONSchema7Type | undefined
    onChange: (value: unknown) => void
}) {
    const [, , , , appID] = useAppState(
        state => state.router.location.pathname
    ).split("/")

    const clientContext = useNeqlContextProvider("turbine")

    const fetchTests = turbine.useFetchAllTrainingTestsQuery({
        variables: {
            trainingTestsAppUuid: appID
        },
        client: clientContext
    })

    const fetchTestsResult = useMemo(
        () => fetchTests.data?.trainingTestsForOwner || [],
        [fetchTests.data]
    )

    const labels = useMemo(
        () => fetchTestsResult?.map(test => test?.taskInfo?.title),
        [fetchTestsResult]
    )
    const handleSearch = useCallback(
        (selected: string) => {
            if (selected) {
                const item = fetchTestsResult.find(
                    c => c?.taskInfo?.title === selected
                )

                if (item?.trainingId || defaultValue) {
                    onChange(item?.trainingId ?? defaultValue)
                }
            }
        },
        [fetchTestsResult, defaultValue, onChange]
    )
    const handleChange = useCallback(
        (index: number) => {
            const item = fetchTestsResult[index]

            if (item === undefined) {
                return
            }

            onChange(item?.trainingId)
        },
        [fetchTestsResult, onChange]
    )

    if (readOnly) {
        const label = fetchTestsResult.find(
            c => c?.trainingId === value?.toString()
        )?.taskInfo?.title
        return label ? (
            <TextInput
                singleLine
                readOnly
                enableMarkdown={false}
                enableCopyTextButton={false}
                value={label}
                inputClassName="rounded-lg bg-transparent py-2 px-3 text-sm leading-5"
                className="rounded-lg border border-rhino-300"
            />
        ) : null
    }

    return (
        <ExclusiveChoiceDropdown
            choices={labels}
            renderChoice={label => label}
            selectedIndex={fetchTestsResult.findIndex(
                c => c?.trainingId === value?.toString()
            )}
            onClick={handleChange}
            onSearch={handleSearch}
            className="space-y-1"
            inputClassName="text-sm leading-6 py-2.5 border-rhino-300"
            testId="exclusive-choice-input"
        />
    )
}
